export type AnnotationSaveState = Readonly<{
  status: 'saved' | 'saving' | 'unsaved' | 'error';
  pending: boolean;
  error?: unknown;
}>;

type AnnotationSaveQueueOptions = {
  save: (xfdf: string) => Promise<void>;
  onState: (state: AnnotationSaveState) => void;
};

/** Own one queue per document/user and pass already captured, immutable XFDF. */
export function createAnnotationSaveQueue({ save, onState }: AnnotationSaveQueueOptions) {
  let state: AnnotationSaveState = { status: 'saved', pending: false };
  let latest: string | undefined;
  let persisted: string | undefined;
  let saving = false;
  let failed = false;
  let failure: unknown;
  let detached = false;
  const waiters = new Set<{
    resolve: () => void;
    reject: (error: unknown) => void;
  }>();

  const publish = (next: AnnotationSaveState) => {
    state = next;
    if (!detached) onState(next);
  };

  const settleWaiters = (error?: { cause: unknown }) => {
    const settled = [...waiters];
    waiters.clear();
    for (const waiter of settled) {
      if (error) waiter.reject(error.cause);
      else waiter.resolve();
    }
  };

  const drain = () => {
    if (saving || failed) return;
    if (latest === undefined || latest === persisted) {
      publish({ status: 'saved', pending: false });
      settleWaiters();
      return;
    }

    const snapshot = latest;
    saving = true;
    publish({ status: 'saving', pending: true });
    void (async () => {
      try {
        await save(snapshot);
      } catch (error) {
        saving = false;
        failed = true;
        failure = error;
        // A lost response can mean the server accepted this write; reassert the
        // newest snapshot on retry even if it matches an older acknowledgement.
        persisted = undefined;
        publish({ status: 'error', pending: true, error });
        settleWaiters({ cause: error });
        return;
      }

      persisted = snapshot;
      saving = false;
      // A response only acknowledges its own snapshot, never edits made later.
      drain();
    })();
  };

  const flush = (): Promise<void> => {
    if (failed) return Promise.reject(failure);
    if (!saving && (latest === undefined || latest === persisted)) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      waiters.add({ resolve, reject });
    });
  };

  return {
    getState: (): AnnotationSaveState => state,
    enqueue(xfdf: string) {
      if (detached || xfdf === latest) return;
      latest = xfdf;
      failed = false;
      failure = undefined;
      publish({ status: saving ? 'saving' : 'unsaved', pending: true });
      drain();
    },
    flush,
    retry(): Promise<void> {
      if (detached) return flush();
      failed = false;
      failure = undefined;
      drain();
      return flush();
    },
    detach() {
      // Captured snapshots still belong to this queue's original document.
      detached = true;
    },
  };
}
